/**
 * topTracts.ts — ranking core for the "Top tracts in view" list (T-222 / BEAD-0040)
 *
 * Pure, framework-free logic: given the tract features the map already holds
 * (`map.querySourceFeatures('tracts')`), produce the ranked top-N list. Kept free of
 * MapLibre/React so the ranking maths is unit-testable with plain objects; the hook
 * (useTopTracts) and the list body (TopTractsList) wire it to the live map.
 *
 * Grounding (design proposal §2/§6, appendix source map):
 *  - Every in-view tract is already client-side with its scores — no backend needed.
 *  - Market Pressure sorts ASCENDING: a low CMPI is an open, uncrowded market = the good
 *    case (the T-199 inverse-index semantics). Sorting it descending would rank the most
 *    saturated markets as "top", the exact colour-lies-about-score trap this feature kills.
 *  - Home-Charging Optimized (HCO) is included with its NATIVE higher-is-better direction
 *    plus explicit UI labelling (decision I); its raw 0–1 scale is displayed ×100 like the
 *    drill-down component sub-scores.
 *  - The list mirrors the map's active tier + land-use filters so it ranks exactly what is
 *    drawn (decision G1); querySourceFeatures ignores layer filters, so we re-apply in JS.
 */
import type { TractFeatureProps } from './tractModel'

export type SortModeId =
  'opportunity' | 'demand' | 'pricing' | 'market_pressure' | 'gap' | 'home_charging'

export type SortDirection = 'asc' | 'desc'

export interface SortMode {
  id: SortModeId
  /** Dropdown label — carries the direction hint so "which end is good" is never a guess. */
  label: string
  /** Short direction sentence shown as the active-sort caption under the header. */
  directionHint: string
  direction: SortDirection
  /** Raw sort key read from a feature's props (null → sorts to the bottom). */
  valueOf: (p: TractFeatureProps) => number | null
  /** Formats the sort value for the per-row secondary badge (e.g. HCO's 0–1 → 0–100). */
  displayValue: (v: number) => number
  /** Short tag under the per-row secondary badge naming what the value is. */
  badgeLabel: string
  /** Whether this mode's badge colours on the inverted (Market-Pressure) scale. */
  inverted: boolean
}

export const DEFAULT_SORT_MODE_ID: SortModeId = 'opportunity'

const round = (v: number) => Math.round(v)
const round100 = (v: number) => Math.round(v * 100)

/**
 * The 6 sort dimensions, in dropdown order (design §6 + mockup + decision I).
 * Opportunity is the default; Market Pressure is the one ascending (open-market-first) mode.
 */
export const SORT_MODES: SortMode[] = [
  {
    id: 'opportunity',
    label: 'Opportunity Score',
    directionHint: 'Highest Opportunity Score first',
    direction: 'desc',
    valueOf: (p) => nOrNull(p.lvi),
    displayValue: round,
    badgeLabel: 'opportunity',
    inverted: false,
  },
  {
    id: 'demand',
    label: 'Demand Potential',
    directionHint: 'Highest demand potential first',
    direction: 'desc',
    valueOf: (p) => nOrNull(p.cdpi),
    displayValue: round,
    badgeLabel: 'demand',
    inverted: false,
  },
  {
    id: 'pricing',
    label: 'Pricing Power',
    directionHint: 'Highest pricing power first',
    direction: 'desc',
    valueOf: (p) => nOrNull(p.ppi_v11),
    displayValue: round,
    badgeLabel: 'pricing',
    inverted: false,
  },
  {
    id: 'market_pressure',
    label: 'Market Pressure (open market first)',
    directionHint: 'Least market pressure first — a low, open market is the good case',
    direction: 'asc',
    valueOf: (p) => nOrNull(p.cmpi),
    displayValue: round,
    badgeLabel: 'pressure',
    inverted: true,
  },
  {
    id: 'gap',
    label: 'Demand–Supply Gap',
    directionHint: 'Largest demand–supply gap first',
    direction: 'desc',
    valueOf: (p) => nOrNull(p.gap_score),
    displayValue: round,
    badgeLabel: 'gap',
    inverted: false,
  },
  {
    id: 'home_charging',
    // Decision I: native higher-is-better direction, explicitly labelled; pending stakeholder
    // confirmation to keep or invert (a label-only change either way).
    label: 'Home-Charging Optimized (more home charging first)',
    directionHint: 'Most home-charging-optimized first — direction pending confirmation',
    direction: 'desc',
    valueOf: (p) => nOrNull(p.HCO),
    displayValue: round100,
    badgeLabel: 'home chg',
    inverted: false,
  },
]

const SORT_MODE_BY_ID: Record<SortModeId, SortMode> = Object.fromEntries(
  SORT_MODES.map((m) => [m.id, m]),
) as Record<SortModeId, SortMode>

export function sortModeById(id: SortModeId): SortMode {
  return SORT_MODE_BY_ID[id] ?? SORT_MODE_BY_ID[DEFAULT_SORT_MODE_ID]
}

function nOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Derive a tract's tier id from its lvi score — mirrors TractLayer's TIER_EXPR / TRACT_TIERS
 * thresholds so the list's tier filter matches the map's exactly.
 */
export function tierIdFromLvi(lvi: number): string {
  if (lvi >= 70) return 'high'
  if (lvi >= 60) return 'good'
  if (lvi >= 45) return 'moderate'
  if (lvi >= 35) return 'low'
  return 'poor'
}

/**
 * Re-apply the map's active tier + land-use filters in JS (decision G1). An `undefined`
 * filter means "no constraint" (the control is at its default all-selected state); an
 * explicit empty array means "none selected" → nothing passes (matches the drawn map).
 */
export function passesFilters(
  p: TractFeatureProps,
  tierFilter: string[] | undefined,
  typeFilter: string[] | undefined,
): boolean {
  if (tierFilter && !tierFilter.includes(tierIdFromLvi(p.lvi))) return false
  if (typeFilter && !typeFilter.includes(p.dominant_type)) return false
  return true
}

type Ring = number[][]
type PolyCoords = Ring[] | Ring[][]

/** Bounding-box centre [lng, lat] of a (Multi)Polygon — the row-click camera target. */
export function featureCentroid(geometry: {
  type: string
  coordinates: unknown
}): [number, number] {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  const visit = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const x = coords[0] as number
      const y = coords[1] as number
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
      return
    }
    for (const c of coords) visit(c)
  }
  visit(geometry.coordinates as PolyCoords)
  return [(minX + maxX) / 2, (minY + maxY) / 2]
}

export interface RankedRow {
  rank: number
  geoid: string
  props: TractFeatureProps
  /** The active sort mode's raw value for this tract (null → ranked last). */
  sortValue: number | null
  centroid: [number, number]
}

export interface RankInput {
  properties: TractFeatureProps
  geometry: { type: string; coordinates: unknown }
}

export interface RankOptions {
  sortModeId: SortModeId
  tierFilter?: string[]
  typeFilter?: string[]
  limit?: number
}

export const DEFAULT_LIMIT = 10

/**
 * Rank the in-view tract features: dedupe by geoid, mirror the active filters, sort by the
 * chosen mode (nulls last, stable geoid tie-break), then cap at `limit`. Returns the capped
 * rows plus the full post-filter in-view total (for the "Top N of M in view" label).
 */
export function rankTopTracts(
  features: RankInput[],
  opts: RankOptions,
): {
  rows: RankedRow[]
  totalInView: number
} {
  const mode = sortModeById(opts.sortModeId)
  const limit = opts.limit ?? DEFAULT_LIMIT

  // Dedupe by geoid (querySourceFeatures repeats features across tile boundaries),
  // keeping the first occurrence, and mirror the active tier/type filters.
  const seen = new Set<string>()
  const kept: RankInput[] = []
  for (const f of features) {
    const p = f.properties
    if (!p || typeof p.tract_geoid !== 'string') continue
    if (seen.has(p.tract_geoid)) continue
    if (!passesFilters(p, opts.tierFilter, opts.typeFilter)) continue
    seen.add(p.tract_geoid)
    kept.push(f)
  }

  const dir = mode.direction === 'asc' ? 1 : -1
  const sorted = kept
    .map((f) => ({ f, v: mode.valueOf(f.properties) }))
    .sort((a, b) => {
      // Nulls always sink to the bottom regardless of direction.
      if (a.v === null && b.v === null)
        return a.f.properties.tract_geoid < b.f.properties.tract_geoid ? -1 : 1
      if (a.v === null) return 1
      if (b.v === null) return -1
      if (a.v !== b.v) return (a.v - b.v) * dir
      // Deterministic tie-break so ranks are stable across recomputes.
      return a.f.properties.tract_geoid < b.f.properties.tract_geoid ? -1 : 1
    })

  const rows: RankedRow[] = sorted.slice(0, limit).map((entry, i) => ({
    rank: i + 1,
    geoid: entry.f.properties.tract_geoid,
    props: entry.f.properties,
    sortValue: entry.v,
    centroid: featureCentroid(entry.f.geometry),
  }))

  return { rows, totalInView: kept.length }
}
